import { useFormik } from 'formik';
import { Fragment, useMemo, useEffect, useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';

import ErrorActions, { REG_ERRORS } from './components/ErrorActions';
import { RegistrationLayout } from './components/RegistrationLayout';

import {
  Dropdown,
  Form,
  InputCheckbox,
  InputField,
  InputRadio,
  Button,
  InputDatePicker,
  Loader,
} from '@/components';
import useAuth from '@/hooks/useAuth';
import useDialog from '@/hooks/useDialog';
import { usePermutive } from '@/hooks/usePermutive';
import useToast from '@/hooks/useToast';
import useVerificarNombre from '@/hooks/useVerificarNombre';
import ROUTES from '@/router/routes';
import { getInitDataRegister, registerUserData } from '@/services';
import {
  Maybe,
  SelectValue,
  GENDER_ID,
  GenderValues,
  DocumentTypesValues,
  ARG_OR_EXT,
  KeysOf,
} from '@/types';
import { DataPostRegister, ResponseInitRegisterTDE } from '@/types/register';
import {
  registrationSchema,
  datejs,
  createDateFromYear,
  selectSocialNetwork,
  isArgOrExt,
  validateFieldError,
  showErrorToast,
  letterGenderById,
} from '@/utils';
import { SECTION_NAME_FOR_ADS } from '@/utils/ads';
import { DOC_TYPES, TEAM_LIST, TEXTS } from '@/utils/constants';

type FormValues = {
  name: string;
  lastName: string;
  supportedClub: Maybe<SelectValue>;
  docArg: Maybe<DocumentTypesValues>;
  documentType: Maybe<SelectValue>;
  docNumber: string;
  birthDate: Maybe<Date>;
  gender: Maybe<GenderValues>;
  termsConditions: boolean;
  getNotifications: boolean;
  customClubName: string;
  country: Maybe<SelectValue>;
};

type RegistrationParams = DataPostRegister &
  ResponseInitRegisterTDE & { playsInTde: boolean };

export const Registration = () => {
  const [isDNIBlockedEdit, setisDNIBlockedEdit] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const { checkNombre } = useVerificarNombre();
  const { logout } = useAuth();
  const dialog = useDialog();
  const { userInitialData } = useAuth();

  const { trackPageView, isReady: isPermutiveReady } = usePermutive();

  useEffect(() => {
    // Permutive esta cargado
    if (isPermutiveReady) {
      trackPageView({
        section: SECTION_NAME_FOR_ADS.registration,
        kw_premium: userInitialData?.premium,
        kw_edad: userInitialData?.edad_dt,
        kw_sexo: letterGenderById(userInitialData?.idSexo),
        kw_equipo: userInitialData?.club_hincha,
        kw_fecha: userInitialData?.fechaActiva.orden,
        kw_pines: userInitialData?.pinesDisponibles,
        kw_saldo: userInitialData?.saldo,
        kw_puntosporfecha: userInitialData?.puntos_fecha,
        kw_puntosportorneo: userInitialData?.puntos_general,
        kw_posicionporfecha: userInitialData?.posicion_fecha,
        kw_posicionportorneo: userInitialData?.posicion_general,
        kw_recibirinfo: userInitialData?.recibeInformacion,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPermutiveReady]);

  if (userInitialData) {
    navigate(ROUTES.welcome, {
      replace: true,
    });
  }

  if (userInitialData?.tiene_equipo) {
    navigate(ROUTES.myTeam11, {
      replace: true,
    });
  }

  const { mutateAsync: saveRegistration } = useMutation(
    (formData: RegistrationParams) => {
      const { playsInTde, ...data } = formData;

      // With this we are removing unnecesary data from the payload.
      // And then we are casting the object to the type DataPostRegister.
      const parsedData = Object.fromEntries(
        Object.entries(data).filter(
          ([key]) =>
            ![
              'fechaCompEscPrim',
              'fechaCompEscSec',
              'tieneDocArgentino',
            ].includes(key)
        )
      ) as unknown as DataPostRegister;

      return registerUserData(parsedData, false, playsInTde ? 1 : 2);
    }
  );

  const { data: dataInit, isLoading } = useQuery(
    'initialDataRegister',
    async () => await getInitDataRegister(),
    {
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        // Este fix fue hecho porque utilizar GDTApiErrors habria sido muy largo y extenso
        // Las validaciones se correran aca y en el onError.
        if ('codError' in data && 'mensaje' in data) {
          toast.show(data.mensaje as string);
          navigate(ROUTES.basePath, { replace: true });
        }
      },
      onError: (error) => {
        const err = error as Error;
        toast.showError(err);
        navigate(ROUTES.notFound, { replace: true });
      },
    }
  );

  const countriesList = useMemo(() => {
    if (dataInit && dataInit?.paises) {
      return dataInit.paises.map((country) => ({
        label: country.nombre,
        value: country.id,
      }));
    }

    return [];
  }, [dataInit]);

  useEffect(() => {
    if (dataInit) {
      setisDNIBlockedEdit(Boolean(dataInit.blockDNI));
    }
  }, [dataInit]);

  const initialValues = useMemo(() => {
    const country = dataInit?.idPais
      ? countriesList.find((c) => c.value === dataInit.idPais) ?? null
      : null;

    const docArg = dataInit?.tieneDocArgentino
      ? ARG_OR_EXT.arg
      : ARG_OR_EXT.ext;

    let supportedClub: Maybe<SelectValue> = null;

    if (dataInit?.clubId) {
      supportedClub =
        TEAM_LIST.find((team) => team.value === dataInit?.clubId) ?? null;
    } else if (dataInit?.otroClub) {
      supportedClub = TEAM_LIST.find((team) => team.value === -1) ?? null;
    }

    const documentType =
      DOC_TYPES.find((doc) => doc.value === dataInit?.tipoDocumentoId) ??
      DOC_TYPES[0];

    let gender = null;
    if (dataInit?.sexoId === 1) {
      gender = GENDER_ID.hombre;
    } else if (dataInit?.sexoId === 2) {
      gender = GENDER_ID.mujer;
    }

    const birthDate = dataInit?.fechaNacimiento
      ? datejs(dataInit.fechaNacimiento, 'DD/MM/YYYY', 'es').toDate()
      : null;

    const name = dataInit?.nombre ?? '';
    const lastName = dataInit?.apellido ?? '';
    const customClubName = dataInit?.otroClub ?? '';

    let docNumber = '';

    if (dataInit) {
      if (dataInit.tieneDocArgentino && dataInit.nroDocumento) {
        docNumber = `${dataInit.nroDocumento}`;
      } else if (
        !dataInit.tieneDocArgentino &&
        dataInit.nroDocumentoExtranjero
      ) {
        docNumber = dataInit.nroDocumentoExtranjero;
      }
    }

    return {
      name,
      lastName,
      supportedClub,
      docArg,
      documentType,
      docNumber,
      birthDate,
      gender,
      termsConditions: true,
      getNotifications: false,
      customClubName,
      country,
    } satisfies FormValues;
  }, [countriesList, dataInit]);

  useEffect(() => {
    if (dataInit && dataInit.codError === 101) {
      toast.show(TEXTS.dontHavePermits);
      navigate(ROUTES.notFound, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataInit]);

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues,
    validateOnBlur: true,
    validationSchema: registrationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      const isArg = isArgOrExt(values.docArg);
      const {
        birthDate,
        documentType,
        supportedClub,
        gender,
        docArg,
        country,
      } = values;

      const isValidDocumentType = isArg ? documentType : country;
      if (
        birthDate &&
        isValidDocumentType &&
        supportedClub &&
        dataInit &&
        gender &&
        docArg &&
        dataInit?.email
      ) {
        const { fechaCompEscSec } = dataInit;
        const minDatePrimaria = datejs(
          fechaCompEscSec,
          'DD/MM/YYYY',
          'es'
        ).toDate();

        const formData = {
          nombre: values.name,
          apellido: values.lastName,
          ...(isArg &&
            documentType && {
              tipodoc: documentType.value,
              numdoc: values.docNumber,
              numdoc2: values.docNumber,
            }),
          dianac: Number(datejs(birthDate).format('DD')),
          mesnac: Number(datejs(birthDate).format('MM')),
          anionac: Number(datejs(birthDate).format('YYYY')),
          sexo: gender,
          hincha: supportedClub.value,
          hinchaOtro: supportedClub.value === -1 ? values.customClubName : '',
          nroDocumentoExtranjero: !isArg ? values.docNumber : '',
          idPais: !isArg ? `${country?.value}` : '',
          aceptaTerminosYCondiciones: values.termsConditions,
          docArg,
          email: dataInit.email,
          email2: dataInit.email,
          terceros: false,
          sms: false,
        } satisfies DataPostRegister;

        const initRegisterTDE = {
          fechaCompEscPrim: dataInit.fechaCompEscPrim,
          fechaCompEscSec: dataInit.fechaCompEscSec,
          tieneDocArgentino: dataInit.tieneDocArgentino,
        } satisfies ResponseInitRegisterTDE;

        // Los usuarios extranjeros no participan en TDE.
        const playsInTde = isArg
          ? datejs(birthDate).isAfter(datejs(minDatePrimaria))
          : false;

        const payload = {
          ...formData,
          ...initRegisterTDE,
          playsInTde,
        } satisfies RegistrationParams;

        try {
          const response = await saveRegistration(payload);

          if (response) {
            if (response.estado) {
              if (playsInTde) {
                navigate(ROUTES.registrationSchool, {
                  state: formData,
                });
              } else {
                navigate(ROUTES.registrationNameTeam, {
                  state: formData,
                });
              }
            } else {
              const codeError = response.codError as KeysOf<typeof REG_ERRORS>;
              switch (response?.codError) {
                case 100: {
                  const { btnA, btnB } = REG_ERRORS[codeError];
                  dialog.render(
                    <>
                      <ErrorActions
                        btnA={{
                          text: btnA.text,
                          action: () => {
                            dialog.removeDialog();
                            setisDNIBlockedEdit(false);
                          },
                        }}
                        btnB={{
                          text: btnB.text,
                          action: () => logout(),
                        }}
                        message={response.mensaje}
                      />
                    </>
                  );
                  break;
                }
                case 150: {
                  const { btnA, btnB } = REG_ERRORS[codeError];
                  dialog.render(
                    <>
                      <ErrorActions
                        btnA={{
                          text: btnA.text,
                          action: () => {
                            navigate(ROUTES.recoverEmail);
                          },
                        }}
                        btnB={{
                          text: btnB.text,
                          action: () => {
                            dialog.removeDialog();
                          },
                        }}
                        message={response.mensaje}
                      />
                    </>
                  );
                  break;
                }
                default: {
                  showErrorToast(response);
                }
              }
            }
          }
        } catch (error) {
          toast.show(TEXTS.callCenterError);
        }
        setSubmitting(false);
        return;
      }
      setSubmitting(false);
    },
  });

  const {
    values,
    handleSubmit,
    isSubmitting,
    handleBlur,
    handleChange,
    setFieldValue,
    setFieldError,
    setTouched,
    errors,
    touched,
  } = formik;

  const handleBlurArgDoc = () => {
    if (values.docArg === null) {
      setFieldError('docArg', 'Requerido');
    }

    setTouched({ ...touched, docArg: true });
  };

  const runVerificarNombre = (overrides?: { gender?: GenderValues | null }) => {
    if (!isArgOrExt(values.docArg)) return;

    const gender =
      overrides && 'gender' in overrides ? overrides.gender : values.gender;

    checkNombre({
      dni: values.docNumber,
      nombre: values.name,
      apellido: values.lastName,
      genero: letterGenderById(gender?.toString()) as 'M' | 'F' | '',
    });
  };

  const handleBlurGender = () => {
    if (values.gender === null) {
      setFieldError('gender', 'Requerido');
    }

    setTouched({ ...touched, gender: true });
  };

  useEffect(() => {
    if (dataInit && isArgOrExt(initialValues.docArg)) {
      checkNombre({
        dni: initialValues.docNumber,
        nombre: initialValues.name,
        apellido: initialValues.lastName,
        genero: letterGenderById(initialValues.gender?.toString()) as
          | 'M'
          | 'F'
          | '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataInit]);

  const handleChangeNameValue = (name: string, value: string | number) => {
    setFieldValue(name, value);
  };

  const getCalendarMaxDate = useMemo(() => {
    if (dataInit?.anioMaximoRegistracion) {
      return createDateFromYear(dataInit.anioMaximoRegistracion + 1)
        .subtract(1, 'day')
        .toDate();
    }

    return datejs().subtract(6, 'year').toDate();
  }, [dataInit]);

  const isArg = isArgOrExt(values.docArg);

  return (
    <RegistrationLayout
      className="space-y-[1.75rem]"
      titleNavbar="Completá tus datos"
    >
      {<Loader isVisible={isLoading || isSubmitting} timeout={isSubmitting} />}

      <header className="space-y-4">
        <h5 className="text-xl text-gdt-blue-dark hidden lg:block">
          Creá tu cuenta
        </h5>
        {!isLoading && (
          <p className="text-sm text-gdt-gray-2 font-medium">
            Podrás acceder ingresando con tu cuenta de{' '}
            <span className="whitespace-nowrap">
              {selectSocialNetwork(dataInit?.tipoConexion)}
            </span>
            : <strong className="whitespace-nowrap">{dataInit?.email}</strong>
          </p>
        )}
      </header>
      <Form.Form
        className="space-y-[1.75rem]"
        loading={isSubmitting}
        onSubmit={handleSubmit}
      >
        <Form.HorizontalGroup>
          <Form.Field>
            <InputField
              onlyLetters
              behavior="default"
              error={validateFieldError(
                touched.name,
                errors.name,
                values.name.length
              )}
              label="Nombre"
              maxLength={30}
              name="name"
              placeholder="Nombre"
              value={values.name}
              onBlur={(e) => {
                handleBlur(e);
                runVerificarNombre();
              }}
              onChange={handleChange}
            />
          </Form.Field>
          <Form.Field>
            <InputField
              onlyLetters
              behavior="default"
              error={validateFieldError(
                touched.lastName,
                errors.lastName,
                values.lastName.length
              )}
              label="Apellido"
              maxLength={30}
              name="lastName"
              placeholder="Apellido"
              value={values.lastName}
              onBlur={(e) => {
                handleBlur(e);
                runVerificarNombre();
              }}
              onChange={handleChange}
            />
          </Form.Field>
        </Form.HorizontalGroup>
        <InputRadio.Wrapper className="flex flex-col lg:flex-row gap-4 lg:gap-8">
          <InputRadio.Input
            disabled={isDNIBlockedEdit}
            isChecked={values.docArg === ARG_OR_EXT.arg}
            label="Tengo documento argentino"
            name="docArg"
            value={ARG_OR_EXT.arg}
            onBlur={handleBlurArgDoc}
            onChange={(name, value) => {
              setFieldValue('docNumber', '');
              handleChangeNameValue(name, value);
            }}
          />
          <InputRadio.Input
            disabled={isDNIBlockedEdit}
            isChecked={values.docArg === ARG_OR_EXT.ext}
            label="No tengo documento argentino"
            name="docArg"
            value={ARG_OR_EXT.ext}
            onBlur={handleBlurArgDoc}
            onChange={(name, value) => {
              setFieldValue('docNumber', '');
              handleChangeNameValue(name, value);
            }}
          />
        </InputRadio.Wrapper>
        <Form.HorizontalGroup className="gap-0 lg:gap-4 space-y-[1.75rem] lg:space-y-0">
          <Form.Field>
            <Dropdown
              disabled={isDNIBlockedEdit}
              error={validateFieldError(touched.country, errors.country)}
              label={isArg ? 'Tipo de Documento' : 'Nacionalidad'}
              options={isArg ? DOC_TYPES : countriesList}
              selected={isArg ? values.documentType : values.country}
              onBlur={() => {
                if (isArg) {
                  if (values.documentType === null) {
                    setFieldError('documentType', 'Requerido');
                  }

                  setTouched({ ...touched, documentType: true });
                } else {
                  if (values.country === null) {
                    setFieldError('country', 'Requerido');
                  }

                  setTouched({ ...touched, country: true });
                }
              }}
              onChange={(option) => {
                setFieldValue(isArg ? 'documentType' : 'country', option);
                if (isArg) runVerificarNombre();
              }}
            />
          </Form.Field>
          <Form.Field>
            {isArg ? (
              <InputField
                autoComplete="off"
                behavior="currency"
                disabled={isDNIBlockedEdit}
                error={validateFieldError(
                  touched.docNumber,
                  errors.docNumber,
                  values.docNumber.length
                )}
                label="N° de Documento"
                maskConfig={{
                  thousandSeparator: '.',
                }}
                maxLength={11}
                name="docNumber"
                placeholder="Ingresar"
                value={values.docNumber}
                onBlur={(e) => {
                  handleBlur(e);
                  runVerificarNombre();
                }}
                onChange={handleChangeNameValue}
              />
            ) : (
              <InputField
                autoComplete="off"
                behavior="default"
                disabled={isDNIBlockedEdit}
                error={validateFieldError(
                  touched.docNumber,
                  errors.docNumber,
                  values.docNumber.length
                )}
                label="Documento de Identidad"
                maxLength={30}
                name="docNumber"
                placeholder="Ingresar"
                type="text"
                value={values.docNumber}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            )}
          </Form.Field>
        </Form.HorizontalGroup>
        <Form.HorizontalGroup className="gap-0 lg:gap-4 space-y-[1.75rem] lg:space-y-0">
          <Form.Field>
            <InputDatePicker
              error={validateFieldError(touched.birthDate, errors.birthDate)}
              id="birthDate"
              label="Fecha de nacimiento"
              maxDate={getCalendarMaxDate}
              selected={values.birthDate}
              onBlur={handleBlur}
              onChange={(newVal) => {
                setFieldValue('birthDate', newVal);
              }}
            />
          </Form.Field>
          <Form.Field>
            <InputRadio.Wrapper
              className="flex gap-4 lg:mt-[1.5rem!important]"
              error={validateFieldError(touched.gender, errors.gender)}
              label="Género"
            >
              <InputRadio.Input
                isChecked={values.gender === GENDER_ID.hombre}
                label="Hombre"
                name="gender"
                value={GENDER_ID.hombre}
                onBlur={handleBlurGender}
                onChange={(name, value) => {
                  handleChangeNameValue(name, value);
                  runVerificarNombre({ gender: value as GenderValues });
                }}
              />
              <InputRadio.Input
                isChecked={values.gender === GENDER_ID.mujer}
                label="Mujer"
                name="gender"
                value={GENDER_ID.mujer}
                onBlur={handleBlurGender}
                onChange={(name, value) => {
                  handleChangeNameValue(name, value);
                  runVerificarNombre({ gender: value as GenderValues });
                }}
              />
            </InputRadio.Wrapper>
          </Form.Field>
        </Form.HorizontalGroup>
        <Form.HorizontalGroup className="gap-0 lg:gap-4 space-y-[1.75rem] lg:space-y-0">
          <Form.Field className="max-w-full lg:max-w-[50%]">
            <Dropdown
              error={validateFieldError(
                touched.supportedClub,
                errors.supportedClub
              )}
              label="Sos hincha de"
              options={TEAM_LIST}
              selected={values.supportedClub}
              onBlur={() => {
                if (values.supportedClub === null) {
                  setFieldError('supportedClub', 'Requerido');
                }

                setTouched({ ...touched, supportedClub: true });
              }}
              onChange={(option) => {
                setFieldValue('supportedClub', option);
              }}
            />
          </Form.Field>
          {values.supportedClub?.value === -1 && (
            <Form.Field>
              <InputField
                alphaNumericSpecialCharacters
                behavior="default"
                error={validateFieldError(
                  touched.customClubName,
                  errors.customClubName,
                  values.customClubName.length
                )}
                label="Otro Club"
                maxLength={30}
                name="customClubName"
                placeholder="Ingrese"
                value={values.customClubName}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Form.Field>
          )}
        </Form.HorizontalGroup>
        <div className="space-y-4">
          <InputCheckbox
            error={errors.termsConditions}
            isChecked={values.termsConditions}
            label="He leído y acepto los términos y condiciones establecidos en el Reglamento de Gran DT."
            name="termsConditions"
            onChange={(name, value) => setFieldValue(name, value)}
          />
          <InputCheckbox
            isChecked={values.getNotifications}
            label={
              <Fragment>
                <span>
                  Acepto recibir notificaciones de terceros acerca de consumos,
                  promociones, productos y servicios. Ver{' '}
                  <Link
                    className="text-gdt-blue font-bold"
                    id="linkPolitics"
                    target="_blank"
                    to="/pdf/Reglamento_Gran_DT.pdf#page=23"
                  >
                    Política de Privacidad.
                  </Link>
                </span>
              </Fragment>
            }
            name="getNotifications"
            onChange={(name, value) => setFieldValue(name, value)}
          />
        </div>
        <div className="flex flex-col space-y-[1.75rem] lg:space-y-0">
          <div className="text-center lg:order-1">
            <button
              className="bg-transparent underline text-gdt-blue hover:text-gdt-blue-dark text-sm font-bold lg:mt-[1.75rem]"
              type="button"
              onClick={() => logout()}
            >
              ¿Ya tenés una cuenta? Iniciá sesión
            </button>
          </div>
          <div className="w-full lg:max-w-[300px] mx-auto lg:order-0">
            <Button className="w-full" type="submit" variant="primary">
              Continuar
            </Button>
          </div>
        </div>
      </Form.Form>
    </RegistrationLayout>
  );
};
